import {useEffect, useState} from 'react'
import {Emoji, NormalizedParticipant, Participants} from 'src/app/types'
import {useAppSelector} from 'src/hooks'
import {normalizeParticipantData} from 'src/utils/normalizeCSV'
import Table from 'src/components/Leaderboard/Table'
import LeaderboardFooter from 'src/components/Leaderboard/LeaderboardFooter'
import FilterDropdown from 'src/components/Dropdown/FilterDropdown'
import {selectGroupNames} from 'src/app/hostSettings'
import {selectAllSessions, sessionNames} from 'src/app/allSessions'
import {formatDateTime, mergeParticipantsData} from 'src/utils/helpers'

const Leaderboard = () => {
    const [isAscending, setIsAscending] = useState(false)
    const [participantData, setParticipantData] = useState<
        NormalizedParticipant[]
    >([])
    const groupNames = useAppSelector(selectGroupNames)
    const [selectedGroups, setSelectedGroups] = useState<string[]>(
        []
    )
    const [selectedBadges, setSelectedBadges] = useState<string[]>([])
    const allSessionParticipants = useAppSelector(selectAllSessions)
    const allSessionNames = useAppSelector(sessionNames)
    const [isTagsFilter, setIsTagsFilter] = useState<boolean>(true)
    const [selectedSessionNames, setSelectedSessionNames] = useState<string[]>(
        allSessionNames
    )
    const [selectedSessionDates, setSelectedSessionDates] = useState<string[]>([])
    const [allSessionDates, setAllSessionDates] = useState<string[]>([])
    const [participants, setParticipants] = useState<Participants>({})
    const [allBadges, setAllBadges] = useState<Emoji[] | string[]>([])

    // This useEffect will first load and merge all participants from all sessions and all groups
    // For when badge is filtered, we have to recalculate the value
    useEffect(() => {
        const AllData: Participants[] = []
        if (selectedGroups.length > 0) {
            const selectedGroupIds = selectedGroups.map(name => groupNames[name])
            setIsTagsFilter(false)
            for (const sessionId in allSessionParticipants) {
                const session = allSessionParticipants[sessionId]
                if (selectedGroupIds.includes(session.groupId!) && selectedSessionNames.includes(session.name!) && (selectedSessionDates.length === 0 || selectedSessionDates.includes(formatDateTime(session.dateCreated).toString()))) {
                    const {participants} = allSessionParticipants[sessionId]
                    AllData.push(participants)
                }
            }
        } else if (selectedSessionNames.length === 1) {
            for (const sessionId in allSessionParticipants) {
                if(selectedSessionDates.length===0)
                    handleSelectedSessionNames(selectedSessionNames)
                const session = allSessionParticipants[sessionId]
                if (session.dateCreated! && selectedSessionDates.includes(formatDateTime(session.dateCreated).toString())) {
                    const {participants} = allSessionParticipants[sessionId]
                    AllData.push(participants)
                }
            }
        } else {
            for (const sessionId in allSessionParticipants) {
                const session = allSessionParticipants[sessionId]
                if (selectedSessionNames.includes(session.name!)) {
                    const {participants} = allSessionParticipants[sessionId]
                    AllData.push(participants)
                }
            }
        }
        // }
        // Json.stringify is a trick that we can detach reference of data
        const res = mergeParticipantsData(JSON.parse(JSON.stringify(AllData)))
        setParticipants(res)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSessionNames.length, selectedGroups.length, selectedSessionDates.length])


    const handleSelectedSessionNames = (selectedSessionNames: string[] = []) => {
        const sessionDates: string[] = []
        if (selectedSessionNames.length === 1) {
            for (const sessionId in allSessionParticipants) {
                const session = allSessionParticipants[sessionId]
                if (session.name === selectedSessionNames[0]) {
                    if (session.dateCreated != null) {
                        sessionDates.push(formatDateTime(session.dateCreated).toString())
                    }
                }
            }
        }
        setAllSessionDates(sessionDates)
        setSelectedSessionDates(sessionDates)
        setSelectedSessionNames(selectedSessionNames)
    }


    useEffect(() => {
        const {normalizedParticipants} = normalizeParticipantData(
            Object.values(participants),
            new Set(selectedBadges)
        )
        setParticipantData(normalizedParticipants)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBadges.length])

    // After participants from all sessions are merged/filters, we normalize data and send it to Table to render
    useEffect(() => {
        const {normalizedParticipants, allBadges} = normalizeParticipantData(
            Object.values(participants)
        )
        setParticipantData([...normalizedParticipants])
        setAllBadges([...allBadges])
        setSelectedBadges([...allBadges])
    }, [participants])

    const onSortAll = () => {
        setParticipantData((prevState) => {
            const total = prevState.pop()!
            prevState.sort((a, b) => {
                if (!isAscending) return a.All - b.All
                return b.All - a.All
            })
            prevState.push(total)
            return prevState
        })
        setIsAscending(!isAscending)
    }

    return (
        <div>
            <h1 className="text-left sm:text-center my-4 text-base font-bold">
                Leaderboard
            </h1>

            <div className="inline-flex my-4 gap-2">
                <FilterDropdown
                    selectedValues={selectedSessionNames}
                    setSelectedOptions={handleSelectedSessionNames}
                    items={allSessionNames}
                    name={'All Sessions'}
                    selectedText={{
                        singularText: 'Filter by Sessions',
                        pluralText: 'Filter by Sessions',
                    }}
                />
                {
                    allSessionDates.length > 0 &&
                    <FilterDropdown
                        selectedValues={selectedSessionDates}
                        setSelectedOptions={setSelectedSessionDates}
                        items={allSessionDates}
                        name={'All Dates'}
                        selectedText={{
                            singularText: 'Filter by Dates',
                            pluralText: 'Filter by Dates',
                        }}
                    />
                }
                <FilterDropdown
                    selectedValues={selectedGroups}
                    setSelectedOptions={setSelectedGroups}
                    items={Object.keys(groupNames)}
                    name={'All Tags'}
                    selectedText={{
                        singularText: 'Filter by Tags',
                        pluralText: 'Filter by Tags',
                    }}
                    isTags={isTagsFilter}
                />
                <FilterDropdown
                    selectedValues={selectedBadges}
                    setSelectedOptions={setSelectedBadges}
                    items={allBadges}
                    name={'All Badges'}
                    selectedText={{
                        singularText: 'Badge Selected',
                        pluralText: 'Badges Selected',
                    }}
                    isBadge
                />
            </div>

            {!!participantData.length && (
                <Table
                    headers={['Participant', 'All', ...selectedBadges]}
                    data={participantData}
                    onSortAll={onSortAll}
                />
            )}

            <LeaderboardFooter
                headers={selectedBadges as Emoji[]}
                normalizedParticipants={participantData}
            />
        </div>
    )
}

export default Leaderboard
